import { computeQuestionContentValidation } from '../../../helpers/computeQuestionContentValidation';
import { isQuestionCountedForPublish } from '../../../helpers/questionPublishScope';
import {
  collapseWhitespace,
  trimWhitespace,
  validateCategoryDescription,
  validateCategoryName,
  validateDescription,
  validateOptionText,
  validateQuestionDescription,
  validateQuestionText,
  validateTitle,
} from '../../../helpers/validation';
import {
  ANSWER_TYPES,
  CHOICE_BASED_TYPES,
  FORM_ACTIONS,
  FormAction,
  QuestionData,
} from '../../../types';

export interface TrimmedFormInputs {
  trimmedTitle: string;
  titleChanged: boolean;
  trimmedDescription: string;
  descriptionChanged: boolean;
  trimmedCategoryName: string;
  categoryNameChanged: boolean;
  trimmedCategoryDescription: string;
  categoryDescriptionChanged: boolean;
  trimmedQuestions: QuestionData[];
  questionsChanged: boolean;
}

export const trimFormInputs = (
  title: string,
  description: string,
  categoryName: string,
  categoryDescription: string,
  questions: QuestionData[],
): TrimmedFormInputs => {
  const trimmedTitle = collapseWhitespace(title);

  const trimmedDescription = description.trim();

  const trimmedCategoryName = trimWhitespace(categoryName);
  const trimmedCategoryDescription = trimWhitespace(categoryDescription);

  const trimmedQuestions = questions.map((q) => ({
    ...q,
    questionText: trimWhitespace(q.questionText),
    description: trimWhitespace(q.description),
    options: q.options?.map((opt) => ({
      ...opt,
      text: trimWhitespace(opt.text),
      // Preserve textError from inline validation
      textError: opt.textError,
    })),
  }));

  const questionsChanged = trimmedQuestions.some(
    (tq, index) =>
      tq.questionText !== questions[index].questionText ||
      tq.description !== questions[index].description ||
      tq.options?.some(
        (opt, optIndex) =>
          opt.text !== questions[index].options?.[optIndex]?.text,
      ),
  );

  return {
    trimmedTitle,
    titleChanged: trimmedTitle !== title,
    trimmedDescription,
    descriptionChanged: trimmedDescription !== description,
    trimmedCategoryName,
    categoryNameChanged: trimmedCategoryName !== categoryName,
    trimmedCategoryDescription,
    categoryDescriptionChanged:
      trimmedCategoryDescription !== categoryDescription,
    trimmedQuestions,
    questionsChanged,
  };
};

export interface FormValidationResult {
  titleError: string | null;
  descriptionError: string | null;
  categoryNameError: string | null;
  categoryDescriptionError: string | null;
  countryError: boolean;
  hasQuestionErrors: boolean;
  questionsWithInvalidText: QuestionData[];
  questionsWithInvalidDescription: QuestionData[];
  questionsWithoutAnswerType: QuestionData[];
  questionsWithEmptyOptions: QuestionData[];
  questionsWithInvalidOptions: QuestionData[];
  questionsWithMissingDatabaseField: QuestionData[];
}

export const validateForm = (
  action: FormAction,
  isSpeakUpForm: boolean,
  trimmedTitle: string,
  trimmedDescription: string,
  categoryName: string,
  categoryDescription: string,
  selectedCountryValue: string | undefined,
  trimmedQuestions: QuestionData[],
): FormValidationResult => {
  const titleError =
    action !== FORM_ACTIONS.PREVIEW ? validateTitle(trimmedTitle) : null;
  const descriptionError =
    action !== FORM_ACTIONS.PREVIEW
      ? validateDescription(trimmedDescription)
      : null;

  const categoryNameError =
    action !== FORM_ACTIONS.PREVIEW ? validateCategoryName(categoryName) : null;
  const categoryDescriptionError =
    action !== FORM_ACTIONS.PREVIEW
      ? validateCategoryDescription(categoryDescription)
      : null;
  const countryError =
    !isSpeakUpForm && action !== FORM_ACTIONS.PREVIEW && !selectedCountryValue;

  const {
    questionsWithInvalidText,
    questionsWithInvalidDescription,
    questionsWithoutAnswerType,
    questionsWithEmptyOptions,
    questionsWithInvalidOptions,
    questionsWithMissingDatabaseField,
    hasQuestionErrors,
  } = computeQuestionContentValidation(trimmedQuestions, !isSpeakUpForm);

  return {
    titleError,
    descriptionError,
    categoryNameError,
    categoryDescriptionError,
    countryError,
    hasQuestionErrors,
    questionsWithInvalidText,
    questionsWithInvalidDescription,
    questionsWithoutAnswerType,
    questionsWithEmptyOptions,
    questionsWithInvalidOptions,
    questionsWithMissingDatabaseField,
  };
};

export const markQuestionErrors = (
  trimmedQuestions: QuestionData[],
  isCoI?: boolean,
): QuestionData[] =>
  trimmedQuestions.map((q) => {
    const textError = validateQuestionText(q.questionText, isCoI);
    const descError = validateQuestionDescription(q.description);
    const hasTextError = textError !== null;
    const hasDescError = descError !== null;
    const hasAnswerTypeError = !q.questionType;
    const hasDatabaseFieldError =
      q.questionType === ANSWER_TYPES.DATABASE_FIELD &&
      !q.databaseFieldOptions?.selectedFieldId;
    const hasEmptyOptions =
      q.questionType &&
      CHOICE_BASED_TYPES.has(q.questionType) &&
      (!q.options ||
        q.options.length === 0 ||
        q.options.some(
          (opt) => !opt.isOtherOption && (!opt.text || opt.text.trim() === ''),
        ));

    const fieldName =
      q.questionType === ANSWER_TYPES.DATA_GRID
        ? 'Column header'
        : 'Option text';

    // Validate each option and check for duplicates
    const updatedOptions =
      q.questionType && CHOICE_BASED_TYPES.has(q.questionType)
        ? q.options?.map((opt, index, allOptions) => {
            // Skip validation for "Other" option - it's system-controlled
            if (opt.isOtherOption) {
              return { ...opt, textError: null };
            }

            // First check basic validation (empty, length)
            const basicError = validateOptionText(opt.text, fieldName);

            // If there's a basic error, return it immediately (no need to check duplicates)
            if (basicError) {
              return {
                ...opt,
                textError: basicError,
              };
            }

            // Check for duplicates (case-insensitive)
            const trimmedText = opt.text.trim().toLowerCase();

            // If this option already has a duplicate error from inline validation, preserve it
            if (opt.textError && opt.textError.includes('must be unique')) {
              return {
                ...opt,
                textError: opt.textError,
              };
            }

            // Check if there's another option with same text that already has the error
            const anotherHasError = allOptions.some(
              (other, otherIndex) =>
                otherIndex !== index &&
                !other.isOtherOption &&
                other.text.trim().toLowerCase() === trimmedText &&
                other.textError &&
                other.textError.includes('must be unique'),
            );

            // If another option already has the error, don't mark this one
            if (anotherHasError) {
              return {
                ...opt,
                textError: null,
              };
            }

            // Otherwise, check if this is a duplicate of an earlier option
            const isDuplicate =
              trimmedText &&
              allOptions.some(
                (other, otherIndex) =>
                  otherIndex < index &&
                  !other.isOtherOption &&
                  other.text.trim().toLowerCase() === trimmedText,
              );

            return {
              ...opt,
              textError: isDuplicate ? `${fieldName} must be unique` : null,
            };
          })
        : q.options;

    const hasInvalidOptions =
      q.questionType &&
      CHOICE_BASED_TYPES.has(q.questionType) &&
      updatedOptions &&
      updatedOptions.some((opt) => opt.textError !== null);

    const hasError =
      hasTextError ||
      hasDescError ||
      hasAnswerTypeError ||
      hasDatabaseFieldError ||
      Boolean(hasEmptyOptions) ||
      Boolean(hasInvalidOptions);

    const hasNoOptions =
      q.questionType &&
      CHOICE_BASED_TYPES.has(q.questionType) &&
      (!q.options || q.options.length === 0);

    const itemNoun =
      q.questionType === ANSWER_TYPES.DATA_GRID ? 'columns' : 'options';
    const optionsError = hasNoOptions
      ? `Please add ${itemNoun} to continue`
      : null;

    return {
      ...q,
      textTouched: true,
      descriptionTouched: true,
      answerTypeTouched: true,
      databaseFieldTouched:
        q.questionType === ANSWER_TYPES.DATABASE_FIELD
          ? true
          : q.databaseFieldTouched,
      isExpanded: hasError,
      options: updatedOptions,
      optionsError,
    };
  });

export function countAddedQuestionsForPublish(
  questions: QuestionData[],
): number {
  return questions.filter(isQuestionCountedForPublish).length;
}
